import { useCallback, useContext, useMemo } from 'react';

import type { PayloadAction } from '@reduxjs/toolkit';
import { useNodes } from '@xyflow/react';
import { WarningMessages } from 'constants/Warning.constants';
import { useFlowId } from 'contexts/FlowIdContext';
import { useStreamFunctions } from 'stream/globalFunction/useStreamFunctions';
import { useShallow } from 'zustand/react/shallow';

import type { CreateFlowStepPayload } from '@polyai/stream/commands/handlers/flow/create-flow-step/schema';
import type { CreateTransitionFunctionPayload } from '@polyai/stream/commands/handlers/flow/create-transition-function/schema';
import {
  createFlowStep,
  createFlowTransitionFunction,
  flowFunctionsSelectors,
  flowsSelectors,
  stepsSelectors,
} from '@polyai/stream/store/reducers/flows';
import {
  AddIcon,
  ExitIcon,
  StepTransitionIcon,
} from '@polyai/ui/components/atoms/Icons';
import { PopupMenuItem } from '@polyai/ui/components/molecules/PopupMenu';
import { baseColors } from '@polyai/ui/styles/config/colors';

import { FLOW_TRANSITION_PREFIX } from 'components/molecules/LexicalEditor/features/action/constants';
import type {
  ActionTagContentProps,
  ActionTypeInput,
  ActionTypeOutput,
} from 'components/molecules/LexicalEditor/features/action/types';
import { generateDefaultFunctionCode } from 'components/molecules/RichTextInput/utils';
import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { useAppSelector } from 'hooks/reduxHooks';
import { getUniqueName } from 'hooks/useFlows/useFlows';
import useProjectRoute from 'hooks/useProjectRoute';
import { getActionName } from 'lib/ddlogHelper';
import { generateStepId, generateTransitionId } from 'lib/prettyUuid';
import { StepNodeContext } from 'screens/FlowDetails/components/StepNode/StepNodeProvider';
import { positionNewNode } from 'screens/FlowDetails/helpers';
import {
  functionIsExit,
  getAdvancedNode,
  getDefaultTransition,
} from 'screens/FlowDetails/hooks/helpers';
import useFlowDetailsStore, {
  reactFlowSelector,
} from 'screens/FlowDetails/hooks/useFlowDetailsStore';
import useFlowState from 'screens/FlowDetails/hooks/useFlowState';
import { useViewMode } from 'screens/withPermissions/useViewMode';

export type AddStepOnChange = (
  fn: CreateTransitionFunctionPayload['transitionFunction'],
) => void;

export const useAddStepFunction = (onChange?: AddStepOnChange) => {
  const dispatchEvents = useServerDispatch();
  const { flowId } = useProjectRoute<{
    flowId: string;
  }>();
  const activeNodes = useNodes();
  const { activeStep } = useContext(StepNodeContext);

  const { activeFunctions } = useStreamFunctions();
  const { flow, flowFunctions } = useFlowState();
  const existingStepNames = useMemo(
    () => [...stepsSelectors.selectAll(flow.steps).map(s => s.name)],
    [flow.steps],
  );

  const existingFuncNames = useMemo(
    () => [...flowFunctions, ...activeFunctions].map(f => f.name),
    [activeFunctions, flowFunctions],
  );

  const addStepTransition = useCallback(
    (step?: string, defaultFunctionName?: string) => {
      if (!activeStep) {
        return;
      }

      const stepName = step ?? getUniqueName(existingStepNames, 'Step');

      const functionName = getUniqueName(
        existingFuncNames,
        defaultFunctionName ??
          `go_to_${stepName.replaceAll(' ', '_').toLowerCase()}`,
        '_',
      );

      let createStepPayload: CreateFlowStepPayload | undefined = undefined;
      const events: PayloadAction<any>[] = [];
      if (!existingStepNames.includes(stepName)) {
        const newStepId = generateStepId();
        createStepPayload = {
          flowId,
          step: {
            id: newStepId,
            name: stepName,
            prompt: '',
            position: { x: 0, y: 0 },
            references: {
              sms: {},
              handoff: {},
              attributes: {},
              entities: {},
              transitionFunctions: {},
              globalFunctions: {},
              variables: {},
            },
          },
        };
        const node = positionNewNode(
          getAdvancedNode(createStepPayload?.step),
          activeNodes,
          activeStep,
        );
        createStepPayload.step.position = node.position;
        events.push(createFlowStep(createStepPayload));
      }

      const newFuncId = generateTransitionId();
      const newFunc = getDefaultTransition({
        id: newFuncId,
        transitionName: functionName,
        toStepName: stepName,
        fromStepId: activeStep,
      });
      events.push(
        createFlowTransitionFunction({
          flowId,
          transitionFunction: newFunc,
        }),
      );

      void dispatchEvents(...events);

      onChange?.(newFunc);
    },
    [
      activeStep,
      existingStepNames,
      existingFuncNames,
      flowId,
      dispatchEvents,
      onChange,
      activeNodes,
    ],
  );

  const addExitFlow = useCallback(
    (name: string) => {
      if (!activeStep) {
        return;
      }

      const newFunc: CreateTransitionFunctionPayload['transitionFunction'] = {
        id: generateTransitionId(),
        name,
        code: generateDefaultFunctionCode({
          functionName: name,
          functionType: 'flow_function',
          code: ' conv.exit_flow()',
        }),
        archived: false,
        parameters: [],
        description: `Exit the ${flow!.name} flow`,
        errors: [],
        latencyControl: {
          initialDelay: 0,
          interval: 0,
          delayResponses: [],
          enabled: false,
        },
        references: {
          flowSteps: {
            [activeStep]: true,
          },
          variables: {},
        },
      };

      void dispatchEvents(
        createFlowTransitionFunction({
          flowId,
          transitionFunction: newFunc,
        }),
      );
      onChange?.(newFunc);
    },
    [activeStep, dispatchEvents, flow, flowId, onChange],
  );
  return { addStepTransition, addExitFlow };
};

const FlowTransitionTagContent = ({
  id,
  closePopup,
  remove,
}: ActionTagContentProps) => {
  const { flowId } = useProjectRoute<{
    flowId: string;
  }>();
  const flow = useAppSelector(state =>
    flowsSelectors.selectById(state.flows.flows, flowId),
  );
  const { isEditable } = useViewMode();

  if (!flow) {
    throw new Error('Must be within the flows page to use transitions');
  }

  const func = useMemo(
    () => flowFunctionsSelectors.selectById(flow.transitionFunctions, id),
    [flow.transitionFunctions, id],
  );

  const { openFlowFunction } = useFlowDetailsStore(
    useShallow(reactFlowSelector),
  );

  const { addStepTransition } = useAddStepFunction();

  return func && !func.archived ? (
    isEditable ? (
      <>
        <PopupMenuItem
          data-dd-action={getActionName('Flow', 'edit', 'function')}
          data-test-id="open-flow-function"
          onClick={() => {
            openFlowFunction(func.id, undefined, (_newName: string) => {});
            closePopup?.();
          }}
        >
          Edit
        </PopupMenuItem>
        <PopupMenuItem
          data-dd-action={getActionName('Flow', 'delete', 'function')}
          data-test-id="remove-flow-function"
          isDelete
          onClick={remove}
        >
          Remove
        </PopupMenuItem>
      </>
    ) : (
      <PopupMenuItem
        data-dd-action={getActionName('Flow', 'open', 'function')}
        data-test-id="open-flow-function"
        onClick={() => {
          openFlowFunction(func.id, undefined, (_newName: string) => {});
          closePopup?.();
        }}
      >
        View
      </PopupMenuItem>
    )
  ) : (
    isEditable && (
      <>
        <PopupMenuItem
          data-dd-action={getActionName('Flow', 'delete', 'function')}
          data-test-id="remove-flow-function"
          isDelete
          onClick={remove}
        >
          Remove
        </PopupMenuItem>
        <PopupMenuItem
          data-dd-action={getActionName('Flow', 'create', 'function')}
          icon={<AddIcon size="sm" />}
          isCreate
          onClick={() => addStepTransition(undefined, id)}
        >
          Create transition
        </PopupMenuItem>
      </>
    )
  );
};

export const useFlowTransitionActionType = ({
  id,
}: ActionTypeInput): ActionTypeOutput => {
  const flowId = useFlowId();
  const flow = useAppSelector(state =>
    flowsSelectors.selectById(state.flows.flows, flowId),
  );
  const func = useAppSelector(state => {
    const flow = flowsSelectors.selectById(state.flows.flows, flowId);
    if (!flow) {
      return undefined;
    }
    return flowFunctionsSelectors.selectById(flow.transitionFunctions, id);
  });

  const isExitFunction = func && functionIsExit(func?.code);

  const warning =
    !func || func.archived
      ? WarningMessages.FUNCTION_NOT_EXIST
      : func.errors?.length
        ? WarningMessages.FUNCTION_HAS_ERRORS
        : undefined;

  return {
    Content: FlowTransitionTagContent,
    name: func?.name ?? id,
    Icon: isExitFunction ? ExitIcon : StepTransitionIcon,
    backgroundColor: isExitFunction
      ? baseColors.yellow['05']
      : baseColors.blue['05'],
    iconColor: isExitFunction ? 'iconWarning' : 'iconInfo',
    textColor: isExitFunction ? 'textWarning' : 'iconInfo',
    warning: warning,
    prefix: FLOW_TRANSITION_PREFIX,
    isEnabled: !!flow,
    isLoading: !func,
  };
};
